





















import Vue from "vue";

import { DictationTile, DictationSelection } from "../../models/control";

import JzButton from "./JzButton.vue";
import JzDictationDisplay from "./JzDictationDisplay.vue";
import JzDictationOptions from "./JzDictationOptions.vue";
import { shuffleArray } from "../../utils/common";

interface Data {
  nextSelectionIndex: number;
  currentSelection: DictationSelection;
  currentOptions: DictationTile[];
  submitted: boolean;
}

interface Methods {
  findNextSelectionIndex: () => void;
  onSend: () => void;
  onTileSelected: (tile: DictationTile) => void;
  onTileUnselected: (tile: DictationTile) => Promise<void>;
  onTileUnselectedFromDisplay: (tile: DictationTile) => Promise<void>;
  onTileUnselectedFromOptions: (tile: DictationTile) => Promise<void>;
}

interface Computed {
  isFinished: boolean;
}

interface Props {
  // If true, when all options are selected, options cannot be unselected
  lockFinishedSelection: boolean;
  msgSendText: string;
  options: DictationTile[];
  // If true, shuffle the options tiles
  shuffle: boolean;
}

/**
 * @emits dictation-select(tileAfterSelect: DictationTile) payload includes
 * selected index
 * @emits dictation-unselect-display(tileBeforeUnselect: DictationTile) payload
 * includes formerly selected index
 * @emits dictation-unselect-options(tileBeforeUnselect: DictationTile) payload
 * includes formerly selected index
 * @emits dictation-send
 */
export default Vue.extend<Data, Methods, Computed, Props>({
  name: "JzDictationInput",
  components: { JzButton, JzDictationDisplay, JzDictationOptions },

  props: {
    lockFinishedSelection: { type: Boolean, default: false },
    msgSendText: { type: String, default: "Send" },
    options: { type: Array as () => DictationTile[], required: true },
    shuffle: { type: Boolean, default: false }
  },

  data: function() {
    return {
      currentOptions: [],
      currentSelection: new Array(this.options.length).fill(undefined),
      nextSelectionIndex: 0,
      submitted: false
    };
  },

  computed: {
    isFinished() {
      return this.nextSelectionIndex >= this.options.length;
    }
  },

  created() {
    // Process options
    this.options.forEach((opt, idx) => {
      const copiedTile: DictationTile = {
        text: opt.text,
        fixed: opt.fixed
      };
      // Save original index. Original index does not make distinction
      // between fixed and non-fixed tiles
      copiedTile.originalIndex = idx;

      // fixed tile is already selected...
      if (copiedTile.fixed) {
        copiedTile.selectedIndex = idx;
        this.currentSelection[idx] = copiedTile;
      }
      // ...otherwise make it available for selection
      else {
        this.currentOptions = [...this.currentOptions, copiedTile];
      }
    });

    // Optionally shuffle the options
    if (this.shuffle) {
      this.currentOptions = shuffleArray(this.currentOptions);
    }

    this.findNextSelectionIndex();
  },

  methods: {
    findNextSelectionIndex() {
      this.nextSelectionIndex = 0;

      while (this.currentSelection[this.nextSelectionIndex]) {
        this.nextSelectionIndex++;
      }
    },

    onSend() {
      this.submitted = true;
      const userSelection: DictationTile[] = this.currentSelection.filter(
        (t): t is DictationTile => t !== undefined
      );
      // If there are some undefined selection, cancel submission
      if (userSelection.length !== this.currentSelection.length) {
        return;
      }

      this.$emit("dictation-send", userSelection);
    },

    onTileSelected(tile) {
      tile.selectedIndex = this.nextSelectionIndex;
      this.currentSelection[this.nextSelectionIndex] = tile;

      this.findNextSelectionIndex();
      this.$emit("dictation-select", tile);
    },

    async onTileUnselected(tile) {
      if (this.isFinished && this.lockFinishedSelection) {
        return;
      }

      if (tile.selectedIndex !== undefined) {
        Vue.set(this.currentSelection, tile.selectedIndex, undefined);
        tile.selectedIndex = undefined;
      }

      this.findNextSelectionIndex();
    },

    async onTileUnselectedFromDisplay(tile) {
      this.$emit("dictation-unselect-display", tile);
      this.onTileUnselected(tile);
    },

    async onTileUnselectedFromOptions(tile) {
      this.$emit("dictation-unselect-options", tile);
      this.onTileUnselected(tile);
    }
  }
});
